import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
// react component for creating dynamic tables
import ReactTable from "react-table";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import Assignment from "@material-ui/icons/Assignment";
import EditIcon from "@material-ui/icons/Edit";
import PublishIcon from "@material-ui/icons/Publish";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";

// core components
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";

import { voucherApi } from "functions/apis";
import PublicationStatus from "enums/PublicationStatus";
import { GetVoucherTypeValidityTypeDescription } from "enums/VoucherTypeValidityType";
import { formatDateTime, token } from "functions/Helper";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px"
  }
};

const useStyles = makeStyles(styles);

export default function InDraftVoucherTypeList() {
  const source = useRef(new AbortController());

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [filter, setFilter] = React.useState({
    searchString: null,
    publicationStatus: 0,
    sort: 5
  });
  const [loading, setLoading] = React.useState(false);
  useEffect(() => {
    setLoading(true);
    voucherApi
      .get("/VoucherTypes", {
        signal: source.current.signal,
        params: {
          filter: filter,
          pagination: pagination
        }
      })
      .then(data => {
        setData(
          data.records.map(record => {
            return {
              publicationStatusDescription: PublicationStatus(
                record.publicationStatus
              ),
              ...record,
              actions: (
                <div className="actions-right">
                  <Link to={"/admin/VoucherType/Detail/" + record.id}>
                    <Button color="info" className="like">
                      <VisibilityIcon /> VIEW
                    </Button>
                  </Link>
                  {(token["voucher.voucher_types.write"] === "allowed" ||
                    token.is_admin === "True") && (
                    <Link to={"/admin/VoucherType/Edit/" + record.id}>
                      <Button color="info" type="submit" value="Edit">
                        <EditIcon /> EDIT
                      </Button>
                    </Link>
                  )}
                  {(token["voucher.voucher_types.publish"] === "allowed" ||
                    token.is_admin === "True") && (
                    <Link to={"/admin/VoucherType/Publish/" + record.id}>
                      <Button color="info" className="like">
                        <PublishIcon /> PUBLISH
                      </Button>
                    </Link>
                  )}
                  {(token["voucher.voucher_types.write"] === "allowed" ||
                    token.is_admin === "True") && (
                    <Link to={"/admin/VoucherType/Delete/" + record.id}>
                      <Button color="danger" className="remove">
                        <DeleteIcon /> DELETE
                      </Button>
                    </Link>
                  )}
                </div>
              )
            };
          })
        );
        setPages(Math.ceil(data.totalRecords / pagination.pageSize));
        setLoading(false);
      })
      .catch(error => console.log(error));
  }, [pagination, filter]);

  const handleSearchStringChanged = e =>
    setFilter({
      ...filter,
      searchString: e.target.value === "" ? null : e.target.value
    });

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>VOUCHER TYPE - IN DRAFT</h4>
          </CardHeader>
          <CardBody>
            {(token["voucher.voucher_types.write"] === "allowed" ||
              token.is_admin === "True") && (
              <Link to={"/admin/VoucherType/Create"}>
                <Button color="rose" type="button" value="Create">
                  Create Voucher Type
                </Button>
              </Link>
            )}
            <GridItem xs={3}>
              <CustomInput
                labelText="Search"
                formControlProps={{
                  fullWidth: true
                }}
                inputProps={{
                  onChange: handleSearchStringChanged,
                  value: filter.searchString === null ? "" : filter.searchString
                }}
              />
            </GridItem>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) => {
                setPagination({ pageSize: pageSize, pageIndex: page + 1 });
              }}
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                {
                  Header: "Code",
                  accessor: "code"
                },
                {
                  Header: "Name",
                  accessor: "name"
                },
                {
                  id: "includeSurcharge",
                  Header: "Surcharge",
                  accessor: d => (d.includeSurcharge ? "Yes" : "No")
                },
                {
                  id: "includePDF",
                  Header: "PDF",
                  accessor: d => (d.includePDF ? "Yes" : "No")
                },
                {
                  id: "includeTF",
                  Header: "TF",
                  accessor: d => (d.includeTF ? "Yes" : "No")
                },
                {
                  id: "includeCF",
                  Header: "CF",
                  accessor: d => (d.includeCF ? "Yes" : "No")
                },
                {
                  id: "hasMerchant",
                  Header: "Has Merchant",
                  accessor: d => (d.hasMerchant ? "Yes" : "No")
                },
                {
                  id: "validity",
                  Header: "Validity",
                  accessor: d =>
                    d.validity +
                    " " +
                    GetVoucherTypeValidityTypeDescription(d.validityType) +
                    "(s)"
                },
                {
                  id: "createdAt",
                  Header: "Created At",
                  accessor: d => formatDateTime(new Date(d.createdAt + "Z"))
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
